import { useMutation } from '@apollo/client';
import type { IssueTypes } from '@risksmart-app/trpc/src/services/service.types';
import type {
  InsertChildIssueMutation,
  InsertIssueInput,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  InsertChildIssueDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useReportPublicIssueTRPC } from './useReportPublicIssueTRPC';

/**
 * Public "report an issue" submit. Flag-gated wrapper: under V3 it routes to
 * the dedicated `issue.reportPublic` procedure (authorized on
 * `public_issue_form:read`, parent-less — COI-488); on legacy it uses the same
 * Apollo insert the public form has always used. Consumers import THIS, never
 * the `*TRPC` variant.
 */
export const useReportPublicIssue = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertChildIssueGraphQL, graphqlState] = useMutation(
    InsertChildIssueDocument,
    {
      update: (cache) => {
        evictField(cache, 'issue');
        evictField(cache, 'issue_aggregate');
      },
      refetchQueries: [
        namedOperations.Query.getIssueById,
        namedOperations.Query.getIssues,
      ],
    }
  );

  const trpcMutation = useReportPublicIssueTRPC();

  const reportPublicIssue = async (
    variables: InsertIssueInput
  ): Promise<InsertChildIssueMutation> => {
    if (trpcEnabled) {
      return trpcMutation.reportPublicIssue({
        ...variables,
        Type: variables.Type as IssueTypes,
      });
    }

    const result = await insertChildIssueGraphQL({
      variables: { object: variables },
    });
    if (!result.data) {
      throw new Error('Failed to insert issue');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      reportPublicIssue,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    reportPublicIssue,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
